<template>
  <div class="affiliate-page clipr-theme twitch-theme">
    <div class="affiliate-shell">
      <header class="affiliate-header">
        <div class="affiliate-header-copy">
          <span class="affiliate-eyebrow">{{ tt("affiliateProgram.eyebrow", "Clipr Partners") }}</span>
          <div class="affiliate-title-row">
            <h1>{{ tt("affiliateProgram.title", "Programa de Afiliados") }}</h1>
            <button
              type="button"
              class="affiliate-info-btn"
              :aria-label="tt('affiliateProgram.faqOpen', 'Dudas comunes')"
              @click="faqOpen = true"
            >
              <i class="ti-info-alt" aria-hidden="true"></i>
            </button>
          </div>
          <p>{{ tt("affiliateProgram.subtitle", "Recomienda Clipr y gana 30% de cada pago de suscripción.") }}</p>
        </div>
        <router-link class="affiliate-back" to="/user-profile">
          <i class="ti-angle-left"></i>
          {{ tt("affiliateProgram.backToProfile", "Volver al perfil") }}
        </router-link>
      </header>

      <div v-if="errorMessage" class="affiliate-alert affiliate-alert--error">
        <i class="ti-alert"></i>
        {{ errorMessage }}
      </div>

      <section v-if="loading" class="affiliate-card affiliate-loading">
        <i class="ti-reload ti-spin"></i>
        <span>{{ tt("affiliateProgram.loading", "Cargando programa...") }}</span>
      </section>

      <section v-else-if="!enrolled" class="affiliate-enroll">
        <div class="affiliate-enroll-brand">
          <img src="/favicon.svg" alt="" class="affiliate-enroll-logo" aria-hidden="true" />
          <span class="affiliate-enroll-brand-name">clipr.tv</span>
        </div>
        <h2>{{ tt("affiliateProgram.joinTitle", "Convierte tus recomendaciones en ingresos") }}</h2>
        <p>
          {{ tt("affiliateProgram.joinDescription", "Recibe 30% de cada pago durante toda la vida de las suscripciones que refieras. Tu enlace queda listo al instante.") }}
        </p>
        <div class="affiliate-benefits">
          <span><i class="ti-check"></i>{{ tt("affiliateProgram.benefitLifetime", "30% recurrente de por vida") }}</span>
          <span><i class="ti-check"></i>{{ tt("affiliateProgram.benefitHold", "Comisiones pagables después de 30 días") }}</span>
          <span><i class="ti-check"></i>{{ tt("affiliateProgram.benefitPayout", "Pagos manuales por SPEI o PayPal") }}</span>
          <span><i class="ti-check"></i>{{ tt("affiliateProgram.benefitPremiumBonus", "Con tu primer pago de $25 USD recibes una suscripción anual de Clipr Premium sin costo") }}</span>
        </div>
        <label class="affiliate-terms-check">
          <input v-model="termsAccepted" type="checkbox">
          <span>
            {{ tt("affiliateProgram.acceptPrefix", "Acepto los") }}
            <a href="https://clipr.tv/affiliate-terms/" target="_blank" rel="noopener noreferrer">
              {{ tt("affiliateProgram.termsLink", "términos del programa") }}
            </a>.
          </span>
        </label>
        <button
          type="button"
          class="affiliate-primary-btn"
          :disabled="!termsAccepted || enrolling"
          @click="enroll"
        >
          <i class="fa-solid fa-link"></i>
          {{ enrolling
            ? tt("affiliateProgram.enrolling", "Generando...")
            : tt("affiliateProgram.generateLink", "Generar mi enlace") }}
        </button>
      </section>

      <template v-else>
        <section class="affiliate-card affiliate-link-card">
          <header class="affiliate-link-header">
            <div class="affiliate-link-icon" aria-hidden="true">
              <i class="fa-solid fa-link"></i>
            </div>
            <div class="affiliate-link-header-copy">
              <span class="affiliate-section-label">{{ tt("affiliateProgram.yourLink", "Tu enlace personal") }}</span>
              <p class="affiliate-link-subtitle">
                {{ tt("affiliateProgram.lastClick", "Tu enlace se vincula 90 días al usuario. Si se registra en ese periodo, queda atribuido a ti.") }}
              </p>
            </div>
          </header>

          <div class="affiliate-link-zone affiliate-link-zone--share">
            <span class="affiliate-link-zone-label">
              <i class="ti-share" aria-hidden="true"></i>
              {{ tt("affiliateProgram.shareLink", "Compartir enlace") }}
            </span>
            <div class="affiliate-link-share-row">
              <code class="affiliate-link-display">{{ dashboard.referral_url }}</code>
              <button
                type="button"
                class="affiliate-copy-btn"
                :class="{ 'is-copied': copied }"
                @click="copyLink"
              >
                <i :class="copied ? 'ti-check' : 'ti-files'" aria-hidden="true"></i>
                {{ copied ? tt("affiliateProgram.copied", "Copiado") : tt("affiliateProgram.copyLink", "Copiar enlace") }}
              </button>
            </div>
          </div>

          <button
            v-if="!codeEditorOpen"
            type="button"
            class="affiliate-customize-toggle"
            @click="codeEditorOpen = true"
          >
            <i class="ti-pencil" aria-hidden="true"></i>
            {{ tt("affiliateProgram.customizeUrl", "Personalizar URL") }}
          </button>

          <form
            v-else
            class="affiliate-link-zone affiliate-link-zone--edit"
            @submit.prevent="saveReferralCode"
          >
            <label class="affiliate-link-zone-label" for="affiliate-referral-code">
              <i class="ti-pencil" aria-hidden="true"></i>
              {{ tt("affiliateProgram.customizeCode", "Personaliza tu código") }}
            </label>
            <div class="affiliate-code-edit-row">
              <span class="affiliate-code-prefix">clipr.tv/?ref=</span>
              <input
                id="affiliate-referral-code"
                v-model.trim="referralCode"
                type="text"
                maxlength="64"
                autocomplete="off"
                spellcheck="false"
                :placeholder="tt('affiliateProgram.codePlaceholder', 'tu-codigo')"
                aria-describedby="affiliate-code-hint"
              >
              <button
                type="submit"
                class="affiliate-code-save-btn"
                :class="{ 'is-ready': canSaveReferralCode, 'is-saved': codeSaved }"
                :disabled="!canSaveReferralCode"
              >
                {{ savingCode
                  ? tt("affiliateProgram.savingCode", "Guardando...")
                  : codeSaved
                    ? tt("affiliateProgram.codeSaved", "Guardado")
                    : tt("affiliateProgram.saveCode", "Guardar") }}
              </button>
            </div>
            <small id="affiliate-code-hint" class="affiliate-link-zone-hint">
              {{ tt("affiliateProgram.codeHint", "Usa letras, números y guiones. Tus códigos anteriores seguirán funcionando y quedarán reservados para ti.") }}
            </small>
            <button
              type="button"
              class="affiliate-customize-close"
              @click="codeEditorOpen = false"
            >
              {{ tt("affiliateProgram.hideCustomizeUrl", "Ocultar") }}
            </button>
          </form>
        </section>

        <div class="affiliate-dashboard-grid">
          <section class="affiliate-panel">
            <header class="affiliate-panel-header">
              <span class="affiliate-panel-title">
                <i class="ti-bar-chart"></i>
                {{ tt("affiliateProgram.activityTitle", "Actividad") }}
              </span>
              <p>{{ tt("affiliateProgram.activitySubtitle", "Tráfico y conversiones de tu enlace.") }}</p>
            </header>
            <div class="affiliate-stats affiliate-stats--activity">
              <article class="affiliate-stat affiliate-stat--clicks">
                <div class="affiliate-stat-icon"><i class="ti-mouse-alt"></i></div>
                <div class="affiliate-stat-body">
                  <span>{{ tt("affiliateProgram.clicks", "Clicks") }}</span>
                  <strong>{{ number(stats.clicks) }}</strong>
                </div>
              </article>
              <article class="affiliate-stat affiliate-stat--signups">
                <div class="affiliate-stat-icon"><i class="ti-user"></i></div>
                <div class="affiliate-stat-body">
                  <span>{{ tt("affiliateProgram.signups", "Registros") }}</span>
                  <strong>{{ number(stats.signups) }}</strong>
                </div>
              </article>
              <article class="affiliate-stat affiliate-stat--paying">
                <div class="affiliate-stat-icon"><i class="ti-crown"></i></div>
                <div class="affiliate-stat-body">
                  <span>{{ tt("affiliateProgram.payingReferrals", "Referidos de pago") }}</span>
                  <strong>{{ number(stats.paying_referrals) }}</strong>
                </div>
              </article>
            </div>
          </section>

          <section class="affiliate-panel">
            <header class="affiliate-panel-header">
              <span class="affiliate-panel-title">
                <i class="ti-wallet"></i>
                {{ tt("affiliateProgram.earningsTitle", "Ganancias") }}
              </span>
              <p>{{ tt("affiliateProgram.earningsSubtitle", "Comisiones por estado de pago.") }}</p>
            </header>
            <div class="affiliate-stats affiliate-stats--earnings">
              <article class="affiliate-stat affiliate-stat--pending">
                <div class="affiliate-stat-icon"><i class="ti-timer"></i></div>
                <div class="affiliate-stat-body">
                  <span>{{ tt("affiliateProgram.pending", "Pendiente") }}</span>
                  <strong>{{ money(stats.pending_usd) }}</strong>
                </div>
              </article>
              <article class="affiliate-stat affiliate-stat--payable">
                <div class="affiliate-stat-icon"><i class="ti-money"></i></div>
                <div class="affiliate-stat-body">
                  <span>{{ tt("affiliateProgram.payable", "Pagable") }}</span>
                  <strong>{{ money(stats.payable_usd) }}</strong>
                </div>
              </article>
              <article class="affiliate-stat affiliate-stat--paid">
                <div class="affiliate-stat-icon"><i class="ti-check-box"></i></div>
                <div class="affiliate-stat-body">
                  <span>{{ tt("affiliateProgram.paid", "Pagado") }}</span>
                  <strong>{{ money(stats.paid_usd) }}</strong>
                </div>
              </article>
            </div>
          </section>
        </div>

        <section class="affiliate-card affiliate-payout-card">
          <div class="affiliate-payout-main">
            <div class="affiliate-payout-head">
              <span class="affiliate-section-label">{{ tt("affiliateProgram.requestPayout", "Solicitar pago") }}</span>
              <span
                class="affiliate-status-badge"
                :class="canRequestPayout ? 'affiliate-status-badge--payable' : 'affiliate-status-badge--pending'"
              >
                {{ canRequestPayout
                  ? tt("affiliateProgram.payoutReady", "Listo para solicitar")
                  : tt("affiliateProgram.payoutNotReady", "Aún no disponible") }}
              </span>
            </div>
            <strong class="affiliate-payout-amount">{{ money(stats.payable_usd) }}</strong>
            <div class="affiliate-payout-progress">
              <div class="affiliate-payout-progress-track">
                <div
                  class="affiliate-payout-progress-fill"
                  :style="{ width: `${payoutProgress}%` }"
                ></div>
              </div>
              <small>
                {{ tt("affiliateProgram.payoutHint", "Disponible desde $25 USD. Necesitas WhatsApp para enviar la solicitud.") }}
              </small>
            </div>
          </div>
          <button
            type="button"
            class="affiliate-whatsapp-btn"
            :disabled="!canRequestPayout || payoutRequesting"
            @click="requestPayout"
          >
            <i class="fa-brands fa-whatsapp"></i>
            {{ payoutRequesting
              ? tt("affiliateProgram.preparingWhatsApp", "Preparando...")
              : tt("affiliateProgram.openWhatsApp", "Solicitar por WhatsApp") }}
          </button>
        </section>

        <section class="affiliate-card affiliate-table-card">
          <header class="affiliate-section-header">
            <div>
              <span class="affiliate-section-label">{{ tt("affiliateProgram.payoutHistoryTitle", "Historial de pagos") }}</span>
              <p>{{ tt("affiliateProgram.payoutHistorySubtitle", "Consulta el estado de tus solicitudes de pago.") }}</p>
            </div>
          </header>
          <div v-if="dashboard.payouts.length" class="affiliate-table-wrap">
            <table class="affiliate-table">
              <thead>
                <tr>
                  <th>{{ tt("affiliateProgram.requestedAt", "Solicitado") }}</th>
                  <th>{{ tt("affiliateProgram.amount", "Monto") }}</th>
                  <th>{{ tt("affiliateProgram.status", "Estado") }}</th>
                  <th>{{ tt("affiliateProgram.paidAt", "Pagado") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="payout in dashboard.payouts" :key="payout.id">
                  <td :data-label="tt('affiliateProgram.requestedAt', 'Solicitado')">
                    {{ date(payout.requested_at) }}
                  </td>
                  <td
                    class="affiliate-table-money"
                    :data-label="tt('affiliateProgram.amount', 'Monto')"
                  >
                    {{ money(payout.amount) }}
                  </td>
                  <td :data-label="tt('affiliateProgram.status', 'Estado')">
                    <span
                      class="affiliate-status-badge"
                      :class="payoutStatusClass(payout.status)"
                    >
                      {{ payoutStatus(payout.status) }}
                    </span>
                  </td>
                  <td :data-label="tt('affiliateProgram.paidAt', 'Pagado')">
                    {{ payout.paid_at ? date(payout.paid_at) : "—" }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="affiliate-empty">
            <i class="ti-receipt"></i>
            <p>{{ tt("affiliateProgram.noPayouts", "Aún no has solicitado pagos.") }}</p>
          </div>
        </section>

        <section class="affiliate-card affiliate-table-card">
          <header class="affiliate-section-header">
            <div>
              <span class="affiliate-section-label">{{ tt("affiliateProgram.monthlyTitle", "Resumen mensual") }}</span>
              <p>{{ tt("affiliateProgram.monthlySubtitle", "Clicks, registros y comisiones por mes.") }}</p>
            </div>
          </header>
          <div v-if="dashboard.months.length" class="affiliate-table-wrap">
            <table class="affiliate-table affiliate-table--monthly">
              <thead>
                <tr>
                  <th>{{ tt("affiliateProgram.month", "Mes") }}</th>
                  <th>{{ tt("affiliateProgram.clicks", "Clicks") }}</th>
                  <th>{{ tt("affiliateProgram.signups", "Registros") }}</th>
                  <th>{{ tt("affiliateProgram.payingReferralsShort", "De pago") }}</th>
                  <th>{{ tt("affiliateProgram.pending", "Pendiente") }}</th>
                  <th>{{ tt("affiliateProgram.payable", "Pagable") }}</th>
                  <th>{{ tt("affiliateProgram.paid", "Pagado") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="month in dashboard.months" :key="month.month">
                  <td
                    class="affiliate-table-month"
                    :data-label="tt('affiliateProgram.month', 'Mes')"
                  >
                    {{ month.month }}
                  </td>
                  <td :data-label="tt('affiliateProgram.clicks', 'Clicks')">
                    {{ number(month.clicks) }}
                  </td>
                  <td :data-label="tt('affiliateProgram.signups', 'Registros')">
                    {{ number(month.signups) }}
                  </td>
                  <td :data-label="tt('affiliateProgram.payingReferralsShort', 'De pago')">
                    {{ number(month.paying_referrals) }}
                  </td>
                  <td
                    class="affiliate-table-money affiliate-table-money--pending"
                    :data-label="tt('affiliateProgram.pending', 'Pendiente')"
                  >
                    {{ money(month.pending_usd) }}
                  </td>
                  <td
                    class="affiliate-table-money affiliate-table-money--payable"
                    :data-label="tt('affiliateProgram.payable', 'Pagable')"
                  >
                    {{ money(month.payable_usd) }}
                  </td>
                  <td
                    class="affiliate-table-money affiliate-table-money--paid"
                    :data-label="tt('affiliateProgram.paid', 'Pagado')"
                  >
                    {{ money(month.paid_usd) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="affiliate-empty">
            <i class="ti-calendar"></i>
            <p>{{ tt("affiliateProgram.noActivity", "Aún no hay actividad para mostrar.") }}</p>
          </div>
        </section>
      </template>
    </div>

    <div
      v-if="faqOpen"
      class="affiliate-faq-backdrop"
      @click.self="faqOpen = false"
    >
      <section
        class="affiliate-faq-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="affiliate-faq-title"
      >
        <header class="affiliate-faq-header">
          <div>
            <span class="affiliate-section-label">{{ tt("affiliateProgram.faqEyebrow", "Ayuda") }}</span>
            <h2 id="affiliate-faq-title">{{ tt("affiliateProgram.faqTitle", "Dudas comunes") }}</h2>
          </div>
          <button
            type="button"
            class="affiliate-faq-close"
            :aria-label="tt('affiliateProgram.faqClose', 'Cerrar')"
            @click="faqOpen = false"
          >
            <i class="ti-close" aria-hidden="true"></i>
          </button>
        </header>
        <div class="affiliate-faq-list">
          <article v-for="(item, index) in faqItems" :key="index" class="affiliate-faq-item">
            <h3>{{ item.q }}</h3>
            <p>{{ item.a }}</p>
          </article>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { supabase } from "@/supabase";
import {
  enrollAffiliate,
  fetchAffiliateDashboard,
  requestAffiliatePayout,
  updateAffiliateCode,
} from "@/api/affiliate";

export default {
  name: "AffiliateProgram",
  data() {
    return {
      loading: true,
      enrolling: false,
      savingCode: false,
      payoutRequesting: false,
      termsAccepted: false,
      copied: false,
      codeSaved: false,
      codeEditorOpen: false,
      referralCode: "",
      faqOpen: false,
      errorMessage: "",
      dashboard: {
        enrolled: false,
        referral_url: "",
        stats: null,
        months: [],
        payouts: [],
        payout_threshold_usd: 25,
      },
    };
  },
  computed: {
    enrolled() {
      return Boolean(this.dashboard.enrolled);
    },
    stats() {
      return this.dashboard.stats || {
        clicks: 0,
        signups: 0,
        paying_referrals: 0,
        pending_usd: 0,
        payable_usd: 0,
        paid_usd: 0,
      };
    },
    canRequestPayout() {
      return (
        this.dashboard.affiliate?.status === "active"
        && Number(this.stats.payable_usd) >= Number(this.dashboard.payout_threshold_usd)
      );
    },
    canSaveReferralCode() {
      const code = this.referralCode.toLowerCase();
      return (
        !this.savingCode
        && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(code)
        && code !== String(this.dashboard.affiliate?.code || "").toLowerCase()
      );
    },
    payoutProgress() {
      const threshold = Number(this.dashboard.payout_threshold_usd) || 25;
      const payable = Number(this.stats.payable_usd) || 0;
      return Math.min(100, (payable / threshold) * 100);
    },
    faqItems() {
      return [
        {
          q: this.tt("affiliateProgram.faq.q1", "¿Cuánto gano?"),
          a: this.tt(
            "affiliateProgram.faq.a1",
            "Ganas el 30% de cada pago que haga la persona que invites."
          ),
        },
        {
          q: this.tt("affiliateProgram.faq.q2", "¿Solo me pagan una vez?"),
          a: this.tt(
            "affiliateProgram.faq.a2",
            "No. Cada vez que tu referido pague su plan, tú recibes tu parte. Si sigue pagando mes con mes, tú sigues ganando."
          ),
        },
        {
          q: this.tt("affiliateProgram.faq.q3", "¿Cuándo puedo retirar mi dinero?"),
          a: this.tt(
            "affiliateProgram.faq.a3",
            "Primero esperas 30 días. Después ese dinero ya aparece como pagable y puedes pedir que te lo paguen."
          ),
        },
        {
          q: this.tt("affiliateProgram.faq.q4", "¿Cuánto necesito para pedir un pago?"),
          a: this.tt(
            "affiliateProgram.faq.a4",
            "Necesitas tener al menos $25 USD pagables."
          ),
        },
        {
          q: this.tt("affiliateProgram.faq.q5", "¿Cómo me pagan?"),
          a: this.tt(
            "affiliateProgram.faq.a5",
            "Cuando llegues a $25, toca el botón de WhatsApp en tu panel y envía tu solicitud. Te pagamos por SPEI o PayPal."
          ),
        },
        {
          q: this.tt("affiliateProgram.faq.q6", "¿Cómo uso mi enlace?"),
          a: this.tt(
            "affiliateProgram.faq.a6",
            "Compártelo con quien quieras. Si alguien entra con tu enlace y se registra, queda ligado a ti por 90 días desde su última visita con tu enlace."
          ),
        },
        {
          q: this.tt("affiliateProgram.faq.q7", "¿Qué es pendiente, pagable y pagado?"),
          a: this.tt(
            "affiliateProgram.faq.a7",
            "Pendiente: todavía en espera. Pagable: ya puedes pedir tu pago. Pagado: ya te lo enviamos."
          ),
        },
        {
          q: this.tt("affiliateProgram.faq.q8", "¿Puedo usar mi propio enlace?"),
          a: this.tt(
            "affiliateProgram.faq.a8",
            "No. Solo ganas cuando invitas a otras personas."
          ),
        },
        {
          q: this.tt("affiliateProgram.faq.q9", "¿Quién sí cuenta como referido?"),
          a: this.tt(
            "affiliateProgram.faq.a9",
            "Tiene que ser alguien nuevo que se registre con tu enlace y pague un plan premium. Si ya tenía cuenta o no paga el plan, no ganas comisión."
          ),
        },
      ];
    },
  },
  async mounted() {
    await this.loadDashboard();
  },
  methods: {
    tt(key, fallback) {
      const translated = this.$t(key);
      return translated === key ? fallback : translated;
    },
    money(value) {
      return new Intl.NumberFormat(this.$i18n.locale === "en" ? "en-US" : "es-MX", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(Number(value || 0));
    },
    number(value) {
      return Number(value || 0).toLocaleString(this.$i18n.locale === "en" ? "en-US" : "es-MX");
    },
    date(value) {
      return new Intl.DateTimeFormat(this.$i18n.locale === "en" ? "en-US" : "es-MX", {
        dateStyle: "medium",
      }).format(new Date(value));
    },
    payoutStatus(status) {
      const labels = {
        requested: this.tt("affiliateProgram.payoutRequested", "Solicitado"),
        paid: this.tt("affiliateProgram.payoutPaid", "Pagado"),
        rejected: this.tt("affiliateProgram.payoutRejected", "Rechazado"),
      };
      return labels[status] || status;
    },
    payoutStatusClass(status) {
      const classes = {
        requested: "affiliate-status-badge--requested",
        paid: "affiliate-status-badge--paid",
        rejected: "affiliate-status-badge--rejected",
      };
      return classes[status] || "affiliate-status-badge--default";
    },
    async session() {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;
      if (!session) throw new Error(this.tt("affiliateProgram.authRequired", "Debes iniciar sesión."));
      return session;
    },
    async loadDashboard() {
      this.loading = true;
      this.errorMessage = "";
      try {
        this.dashboard = await fetchAffiliateDashboard(await this.session());
        this.referralCode = this.dashboard.affiliate?.code || "";
      } catch (error) {
        this.errorMessage = error.message;
      } finally {
        this.loading = false;
      }
    },
    async enroll() {
      this.enrolling = true;
      this.errorMessage = "";
      try {
        await enrollAffiliate(await this.session());
        await this.loadDashboard();
      } catch (error) {
        this.errorMessage = error.message;
      } finally {
        this.enrolling = false;
      }
    },
    async copyLink() {
      await navigator.clipboard.writeText(this.dashboard.referral_url);
      this.copied = true;
      window.setTimeout(() => {
        this.copied = false;
      }, 1800);
    },
    async saveReferralCode() {
      if (!this.canSaveReferralCode) return;
      this.savingCode = true;
      this.codeSaved = false;
      this.errorMessage = "";
      try {
        const response = await updateAffiliateCode(
          await this.session(),
          this.referralCode.toLowerCase()
        );
        this.dashboard.affiliate.code = response.code;
        this.dashboard.referral_url = response.referral_url;
        this.referralCode = response.code;
        this.codeSaved = true;
        window.setTimeout(() => {
          this.codeSaved = false;
        }, 1800);
      } catch (error) {
        this.errorMessage = error.message === "Affiliate code is already in use"
          ? this.tt("affiliateProgram.codeTaken", "Ese código ya está reservado.")
          : error.message;
      } finally {
        this.savingCode = false;
      }
    },
    async requestPayout() {
      this.payoutRequesting = true;
      this.errorMessage = "";
      try {
        const response = await requestAffiliatePayout(await this.session());
        window.location.assign(response.whatsapp_url);
      } catch (error) {
        this.errorMessage = error.message;
        this.payoutRequesting = false;
      }
    },
  },
};
</script>

<style scoped>
.affiliate-page {
  min-height: 100vh;
  padding: 0.5rem var(--clipr-layout-mobile-page-gutter) 3rem;
  color: var(--clipr-text-primary);
  background:
    radial-gradient(circle at 0% 0%, var(--clipr-purple-overlay-light) 0%, transparent 42%),
    radial-gradient(circle at 100% 8%, rgba(52, 211, 153, 0.06) 0%, transparent 36%),
    var(--clipr-bg-primary);
}

.affiliate-shell {
  width: min(1120px, 100%);
  margin: 0 auto;
  display: grid;
  gap: 1.1rem;
}

.affiliate-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  padding: 0.35rem 0 0.5rem;
}

.affiliate-header h1 {
  margin: 0;
  font-size: clamp(1.55rem, 3vw, 2.15rem);
  letter-spacing: -0.02em;
}

.affiliate-title-row {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin: 0.3rem 0 0.45rem;
}

.affiliate-info-btn {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border: 1px solid var(--clipr-border-purple);
  border-radius: 999px;
  background: var(--clipr-purple-overlay-soft);
  color: var(--clipr-purple-light);
  cursor: pointer;
  transition: var(--clipr-transition);
  flex-shrink: 0;
}

.affiliate-info-btn:hover {
  background: var(--clipr-purple-overlay-hover);
  color: var(--clipr-text-primary);
}

.affiliate-header p,
.affiliate-section-header p,
.affiliate-panel-header p {
  margin: 0;
  color: var(--clipr-text-secondary);
  font-size: 0.92rem;
  line-height: 1.5;
}

.affiliate-eyebrow,
.affiliate-section-label {
  color: var(--clipr-purple-light);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.affiliate-back {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: var(--clipr-text-secondary);
  text-decoration: none;
  white-space: nowrap;
  padding: 0.45rem 0.75rem;
  border-radius: var(--clipr-radius-md);
  border: 1px solid var(--clipr-border-color);
  background: var(--clipr-bg-card-glass);
  transition: var(--clipr-transition);
}

.affiliate-back:hover {
  color: var(--clipr-text-primary);
  border-color: var(--clipr-border-purple);
  background: var(--clipr-purple-overlay-soft);
}

.affiliate-card,
.affiliate-panel,
.affiliate-stat {
  border: 1px solid var(--clipr-border-color);
  border-radius: var(--clipr-radius-xl);
  background: var(--clipr-bg-card);
  box-shadow: var(--clipr-shadow-card);
}

.affiliate-card {
  padding: 1.35rem;
}

.affiliate-panel {
  padding: 1.15rem;
}

.affiliate-loading {
  min-height: 200px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 0.75rem;
  color: var(--clipr-text-secondary);
}

.affiliate-enroll {
  max-width: 720px;
  margin: 1.5rem auto 2rem;
  text-align: center;
  padding: 0 1rem;
}

.affiliate-enroll h2 {
  margin: 1.1rem 0 0.55rem;
}

.affiliate-enroll p {
  color: var(--clipr-text-secondary);
}

.affiliate-enroll-brand {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.65rem;
}

.affiliate-enroll-logo {
  width: 40px;
  height: 40px;
  display: block;
  object-fit: contain;
  flex-shrink: 0;
}

.affiliate-enroll-brand-name {
  font-size: clamp(1.65rem, 3vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--clipr-text-primary);
  line-height: 1;
}

.affiliate-benefits {
  display: grid;
  gap: 0.6rem;
  margin: 1.4rem auto;
  width: fit-content;
  text-align: left;
}

.affiliate-benefits i {
  color: #34d399;
  margin-right: 0.55rem;
}

.affiliate-terms-check {
  display: flex;
  justify-content: center;
  gap: 0.55rem;
  margin: 1.2rem 0;
  color: var(--clipr-text-secondary);
}

.affiliate-terms-check a {
  color: var(--clipr-purple-light);
}

.affiliate-primary-btn,
.affiliate-copy-btn,
.affiliate-code-save-btn,
.affiliate-whatsapp-btn {
  border: 0;
  border-radius: var(--clipr-radius-md);
  font-weight: 700;
  cursor: pointer;
  transition: var(--clipr-transition);
}

.affiliate-primary-btn {
  padding: 0.85rem 1.25rem;
  color: #fff;
  background: var(--clipr-purple-gradient);
  box-shadow: var(--clipr-shadow-button);
}

.affiliate-primary-btn:hover:not(:disabled) {
  box-shadow: var(--clipr-shadow-button-hover);
  transform: translateY(-1px);
}

button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
}

.affiliate-alert {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  border-radius: var(--clipr-radius-md);
  padding: 0.85rem 1rem;
}

.affiliate-alert--error {
  color: #fecaca;
  background: rgba(127, 29, 29, 0.35);
  border: 1px solid rgba(248, 113, 113, 0.3);
}

.affiliate-link-card {
  display: grid;
  gap: 1rem;
}

.affiliate-link-header {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
}

.affiliate-link-icon {
  width: 48px;
  height: 48px;
  display: grid;
  place-items: center;
  border-radius: var(--clipr-radius-lg);
  color: var(--clipr-purple-light);
  background: var(--clipr-purple-overlay-medium);
  border: 1px solid var(--clipr-border-purple);
  font-size: 1.05rem;
  flex-shrink: 0;
}

.affiliate-link-header-copy {
  display: grid;
  gap: 0.3rem;
  min-width: 0;
}

.affiliate-link-subtitle {
  margin: 0;
  color: var(--clipr-text-tertiary);
  font-size: 0.86rem;
  line-height: 1.45;
}

.affiliate-link-zone {
  display: grid;
  gap: 0.55rem;
  padding: 0.95rem 1rem;
  border-radius: var(--clipr-radius-lg);
  border: 1px solid var(--clipr-border-color);
  background: var(--clipr-bg-elevated);
}

.affiliate-link-zone--share {
  border-color: rgba(139, 92, 246, 0.28);
  background:
    linear-gradient(135deg, var(--clipr-purple-overlay-soft) 0%, transparent 55%),
    var(--clipr-bg-elevated);
}

.affiliate-link-zone--edit {
  border-style: dashed;
  border-color: var(--clipr-border-color);
}

.affiliate-link-zone-label {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--clipr-text-secondary);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.affiliate-link-zone-label i {
  color: var(--clipr-purple-light);
  font-size: 0.9rem;
}

.affiliate-link-share-row {
  display: flex;
  align-items: stretch;
  gap: 0.65rem;
  min-width: 0;
}

.affiliate-link-display {
  flex: 1;
  min-width: 0;
  display: block;
  padding: 0.7rem 0.85rem;
  border-radius: var(--clipr-radius-md);
  background: var(--clipr-bg-card);
  border: 1px solid var(--clipr-border-color);
  font-size: 0.9rem;
  line-height: 1.4;
  overflow-wrap: anywhere;
  color: var(--clipr-text-primary);
}

.affiliate-copy-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.7rem 1rem;
  color: #fff;
  background: var(--clipr-purple-gradient);
  border: 1px solid transparent;
  box-shadow: var(--clipr-shadow-button);
  white-space: nowrap;
  flex-shrink: 0;
}

.affiliate-copy-btn.is-copied {
  color: #052e16;
  background: #4ade80;
  box-shadow: 0 8px 24px rgba(74, 222, 128, 0.22);
}

.affiliate-copy-btn:hover:not(:disabled) {
  box-shadow: var(--clipr-shadow-button-hover);
  transform: translateY(-1px);
}

.affiliate-code-edit-row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: stretch;
  gap: 0.55rem;
  min-width: 0;
}

.affiliate-code-prefix {
  display: flex;
  align-items: center;
  padding: 0 0.75rem;
  border-radius: var(--clipr-radius-md);
  background: var(--clipr-bg-card);
  border: 1px solid var(--clipr-border-color);
  color: var(--clipr-text-tertiary);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.82rem;
  white-space: nowrap;
}

.affiliate-code-edit-row input {
  width: 100%;
  min-width: 0;
  padding: 0.7rem 0.85rem;
  border-radius: var(--clipr-radius-md);
  border: 1px solid var(--clipr-border-purple);
  outline: 0;
  color: var(--clipr-text-primary);
  background: var(--clipr-bg-card);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.92rem;
  box-shadow: inset 0 0 0 1px transparent;
  transition: var(--clipr-transition);
}

.affiliate-code-edit-row input:focus {
  border-color: var(--clipr-purple-light);
  box-shadow: 0 0 0 3px var(--clipr-purple-overlay-soft);
}

.affiliate-code-save-btn {
  padding: 0.7rem 1rem;
  color: var(--clipr-text-tertiary);
  background: var(--clipr-bg-card);
  border: 1px solid var(--clipr-border-color);
  white-space: nowrap;
}

.affiliate-code-save-btn.is-ready {
  color: #fff;
  background: var(--clipr-purple-gradient);
  border-color: transparent;
  box-shadow: var(--clipr-shadow-button);
}

.affiliate-code-save-btn.is-saved {
  color: #052e16;
  background: #4ade80;
  border-color: transparent;
}

.affiliate-code-save-btn.is-ready:hover:not(:disabled),
.affiliate-code-save-btn.is-saved:hover:not(:disabled) {
  box-shadow: var(--clipr-shadow-button-hover);
  transform: translateY(-1px);
}

.affiliate-link-zone-hint {
  color: var(--clipr-text-tertiary);
  font-size: 0.82rem;
  line-height: 1.45;
}

.affiliate-customize-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  width: fit-content;
  padding: 0.55rem 0.85rem;
  color: var(--clipr-purple-light);
  background: transparent;
  border: 1px dashed var(--clipr-border-purple);
  border-radius: var(--clipr-radius-md);
  font-weight: 600;
  font-size: 0.86rem;
  cursor: pointer;
  transition: var(--clipr-transition);
}

.affiliate-customize-toggle:hover {
  background: var(--clipr-purple-overlay-soft);
  border-style: solid;
}

.affiliate-customize-close {
  justify-self: start;
  padding: 0;
  color: var(--clipr-text-tertiary);
  background: transparent;
  border: 0;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--clipr-transition);
}

.affiliate-customize-close:hover {
  color: var(--clipr-text-secondary);
}

.affiliate-dashboard-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.affiliate-panel-header {
  margin-bottom: 0.9rem;
}

.affiliate-panel-title {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-weight: 700;
  font-size: 0.95rem;
  color: var(--clipr-text-primary);
}

.affiliate-panel-title i {
  color: var(--clipr-purple-light);
}

.affiliate-panel-header p {
  margin-top: 0.35rem;
}

.affiliate-stats {
  display: grid;
  gap: 0.75rem;
}

.affiliate-stats--activity,
.affiliate-stats--earnings {
  grid-template-columns: 1fr;
}

.affiliate-stat {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.95rem 1rem;
  transition: var(--clipr-transition);
}

.affiliate-stat:hover {
  border-color: var(--clipr-border-hover);
  transform: translateY(-1px);
}

.affiliate-stat-icon {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: var(--clipr-radius-md);
  font-size: 1rem;
  flex-shrink: 0;
}

.affiliate-stat-body {
  display: grid;
  gap: 0.15rem;
  min-width: 0;
}

.affiliate-stat span {
  color: var(--clipr-text-tertiary);
  font-size: 0.76rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.affiliate-stat strong {
  font-size: 1.28rem;
  letter-spacing: -0.02em;
}

.affiliate-stat--clicks .affiliate-stat-icon {
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.18);
}

.affiliate-stat--signups .affiliate-stat-icon {
  color: #93c5fd;
  background: rgba(59, 130, 246, 0.18);
}

.affiliate-stat--paying .affiliate-stat-icon {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.18);
}

.affiliate-stat--pending {
  border-color: rgba(251, 191, 36, 0.28);
}

.affiliate-stat--pending .affiliate-stat-icon {
  color: #fbbf24;
  background: rgba(251, 191, 36, 0.16);
}

.affiliate-stat--pending strong {
  color: #fde68a;
}

.affiliate-stat--payable {
  border-color: rgba(52, 211, 153, 0.32);
  background: linear-gradient(135deg, rgba(6, 78, 59, 0.22) 0%, var(--clipr-bg-card) 100%);
}

.affiliate-stat--payable .affiliate-stat-icon {
  color: #34d399;
  background: rgba(52, 211, 153, 0.18);
}

.affiliate-stat--payable strong {
  color: #6ee7b7;
}

.affiliate-stat--paid .affiliate-stat-icon {
  color: #67e8f9;
  background: rgba(34, 211, 238, 0.16);
}

.affiliate-stat--paid strong {
  color: #a5f3fc;
}

.affiliate-payout-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.25rem;
  background:
    linear-gradient(135deg, rgba(6, 78, 59, 0.18) 0%, transparent 55%),
    var(--clipr-bg-card);
  border-color: rgba(52, 211, 153, 0.22);
}

.affiliate-payout-main {
  display: grid;
  gap: 0.45rem;
  flex: 1;
  min-width: 0;
}

.affiliate-payout-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.55rem;
}

.affiliate-payout-amount {
  font-size: clamp(1.5rem, 3vw, 2rem);
  letter-spacing: -0.02em;
  color: #6ee7b7;
}

.affiliate-payout-progress {
  display: grid;
  gap: 0.45rem;
  max-width: 420px;
}

.affiliate-payout-progress-track {
  height: 7px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.14);
  overflow: hidden;
}

.affiliate-payout-progress-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #34d399, #4ade80);
  transition: width 0.35s ease;
}

.affiliate-payout-progress small {
  color: var(--clipr-text-tertiary);
  line-height: 1.45;
}

.affiliate-whatsapp-btn {
  padding: 0.85rem 1.15rem;
  color: #052e16;
  background: #4ade80;
  box-shadow: 0 8px 24px rgba(74, 222, 128, 0.22);
  white-space: nowrap;
}

.affiliate-whatsapp-btn:hover:not(:disabled) {
  background: #22c55e;
  transform: translateY(-1px);
}

.affiliate-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.28rem 0.65rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  border: 1px solid transparent;
  white-space: nowrap;
}

.affiliate-status-badge--requested {
  color: #fde68a;
  background: rgba(251, 191, 36, 0.16);
  border-color: rgba(251, 191, 36, 0.32);
}

.affiliate-status-badge--paid {
  color: #bbf7d0;
  background: rgba(34, 197, 94, 0.16);
  border-color: rgba(74, 222, 128, 0.32);
}

.affiliate-status-badge--rejected {
  color: #fecaca;
  background: rgba(239, 68, 68, 0.16);
  border-color: rgba(248, 113, 113, 0.32);
}

.affiliate-status-badge--pending {
  color: #fde68a;
  background: rgba(251, 191, 36, 0.14);
  border-color: rgba(251, 191, 36, 0.28);
}

.affiliate-status-badge--payable {
  color: #bbf7d0;
  background: rgba(34, 197, 94, 0.14);
  border-color: rgba(74, 222, 128, 0.28);
}

.affiliate-status-badge--default {
  color: var(--clipr-text-secondary);
  background: var(--clipr-purple-overlay-soft);
  border-color: var(--clipr-border-color);
}

.affiliate-section-header {
  margin-bottom: 1rem;
}

.affiliate-table-wrap {
  overflow-x: auto;
  border-radius: var(--clipr-radius-lg);
  border: 1px solid var(--clipr-border-color);
}

.affiliate-table {
  width: 100%;
  border-collapse: collapse;
  white-space: nowrap;
}

.affiliate-table th,
.affiliate-table td {
  padding: 0.85rem 1rem;
  text-align: right;
  border-bottom: 1px solid var(--clipr-border-color);
}

.affiliate-table tbody tr:last-child td {
  border-bottom: 0;
}

.affiliate-table tbody tr:hover {
  background: var(--clipr-purple-overlay-faint);
}

.affiliate-table th:first-child,
.affiliate-table td:first-child,
.affiliate-table-month {
  text-align: left;
}

.affiliate-table th {
  color: var(--clipr-text-tertiary);
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: var(--clipr-bg-elevated);
}

.affiliate-table-money {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

.affiliate-table-money--pending {
  color: #fde68a;
}

.affiliate-table-money--payable {
  color: #6ee7b7;
}

.affiliate-table-money--paid {
  color: #a5f3fc;
}

.affiliate-empty {
  display: grid;
  justify-items: center;
  gap: 0.55rem;
  color: var(--clipr-text-secondary);
  text-align: center;
  padding: 2rem 1rem;
}

.affiliate-empty i {
  font-size: 1.6rem;
  color: var(--clipr-text-tertiary);
}

.affiliate-empty p {
  margin: 0;
}

.affiliate-faq-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(4px);
}

.affiliate-faq-modal {
  width: min(640px, 100%);
  max-height: min(82vh, 760px);
  display: grid;
  grid-template-rows: auto 1fr;
  border: 1px solid var(--clipr-border-purple);
  border-radius: var(--clipr-radius-xl);
  background: var(--clipr-bg-card);
  box-shadow: var(--clipr-shadow-card);
  overflow: hidden;
}

.affiliate-faq-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem 1.35rem 1rem;
  border-bottom: 1px solid var(--clipr-border-color);
}

.affiliate-faq-header h2 {
  margin: 0.35rem 0 0;
  font-size: 1.25rem;
  letter-spacing: -0.02em;
}

.affiliate-faq-close {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border: 1px solid var(--clipr-border-color);
  border-radius: var(--clipr-radius-md);
  background: transparent;
  color: var(--clipr-text-secondary);
  cursor: pointer;
  transition: var(--clipr-transition);
  flex-shrink: 0;
}

.affiliate-faq-close:hover {
  color: var(--clipr-text-primary);
  border-color: var(--clipr-border-purple);
  background: var(--clipr-purple-overlay-soft);
}

.affiliate-faq-list {
  overflow-y: auto;
  padding: 0.35rem 1.35rem 1.35rem;
  display: grid;
  gap: 0.85rem;
}

.affiliate-faq-item {
  padding: 0.95rem 1rem;
  border-radius: var(--clipr-radius-lg);
  border: 1px solid var(--clipr-border-color);
  background: var(--clipr-bg-elevated);
}

.affiliate-faq-item h3 {
  margin: 0 0 0.45rem;
  font-size: 0.95rem;
  color: var(--clipr-text-primary);
  line-height: 1.4;
}

.affiliate-faq-item p {
  margin: 0;
  color: var(--clipr-text-secondary);
  font-size: 0.9rem;
  line-height: 1.55;
}

@media (max-width: 900px) {
  .affiliate-dashboard-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .affiliate-page {
    width: 100%;
    min-width: 0;
    padding: 0.75rem 0.75rem 1.5rem;
    box-sizing: border-box;
    overflow-x: hidden;
  }

  .affiliate-shell,
  .affiliate-header-copy,
  .affiliate-dashboard-grid,
  .affiliate-panel,
  .affiliate-card,
  .affiliate-payout-main,
  .affiliate-section-header > div {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .affiliate-shell {
    gap: 0.85rem;
  }

  .affiliate-header {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    padding: 0;
  }

  .affiliate-title-row {
    align-items: flex-start;
    margin-top: 0.25rem;
  }

  .affiliate-header h1 {
    min-width: 0;
    font-size: 1.5rem;
    line-height: 1.15;
    overflow-wrap: anywhere;
  }

  .affiliate-header p,
  .affiliate-section-header p,
  .affiliate-panel-header p {
    font-size: 0.86rem;
    overflow-wrap: anywhere;
  }

  .affiliate-back {
    align-self: flex-start;
  }

  .affiliate-card,
  .affiliate-panel {
    padding: 0.9rem;
    border-radius: var(--clipr-radius-lg);
  }

  .affiliate-enroll {
    width: 100%;
    min-width: 0;
    margin: 0.75rem auto 1.25rem;
    padding: 0;
    box-sizing: border-box;
  }

  .affiliate-enroll h2 {
    font-size: 1.35rem;
    line-height: 1.25;
  }

  .affiliate-enroll p,
  .affiliate-benefits span,
  .affiliate-terms-check span {
    overflow-wrap: anywhere;
  }

  .affiliate-benefits {
    width: 100%;
  }

  .affiliate-terms-check {
    align-items: flex-start;
    justify-content: flex-start;
    text-align: left;
  }

  .affiliate-terms-check input {
    flex: 0 0 auto;
    margin-top: 0.2rem;
  }

  .affiliate-primary-btn {
    width: 100%;
  }

  .affiliate-link-header {
    gap: 0.65rem;
  }

  .affiliate-link-icon {
    width: 40px;
    height: 40px;
    font-size: 0.95rem;
  }

  .affiliate-link-share-row {
    flex-direction: column;
  }

  .affiliate-link-display,
  .affiliate-copy-btn,
  .affiliate-code-edit-row,
  .affiliate-code-prefix,
  .affiliate-code-edit-row input,
  .affiliate-code-save-btn,
  .affiliate-customize-toggle {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .affiliate-copy-btn,
  .affiliate-code-save-btn {
    white-space: normal;
  }

  .affiliate-code-edit-row {
    grid-template-columns: 1fr;
  }

  .affiliate-code-prefix {
    justify-content: center;
    padding: 0.55rem 0.75rem;
    font-size: 0.78rem;
    overflow-wrap: anywhere;
    white-space: normal;
    text-align: center;
  }

  .affiliate-dashboard-grid {
    gap: 0.85rem;
  }

  .affiliate-stat {
    min-width: 0;
    padding: 0.8rem;
  }

  .affiliate-stat-icon {
    width: 38px;
    height: 38px;
  }

  .affiliate-stat-body,
  .affiliate-stat strong,
  .affiliate-stat span {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .affiliate-payout-card {
    flex-direction: column;
    align-items: stretch;
    gap: 0.9rem;
  }

  .affiliate-payout-head {
    align-items: flex-start;
  }

  .affiliate-status-badge {
    max-width: 100%;
    white-space: normal;
    text-align: center;
  }

  .affiliate-copy-btn,
  .affiliate-whatsapp-btn {
    width: 100%;
    white-space: normal;
  }

  .affiliate-table-card {
    padding: 0.85rem;
  }

  .affiliate-table-wrap {
    border: 0;
    overflow: visible;
  }

  .affiliate-table,
  .affiliate-table tbody {
    display: block;
    width: 100%;
    min-width: 0;
    white-space: normal;
  }

  .affiliate-table thead {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
  }

  .affiliate-table tbody {
    display: grid;
    gap: 0.75rem;
  }

  .affiliate-table tbody tr {
    display: block;
    min-width: 0;
    border: 1px solid var(--clipr-border-color);
    border-radius: var(--clipr-radius-md);
    background: var(--clipr-bg-elevated);
    overflow: hidden;
  }

  .affiliate-table td,
  .affiliate-table td:first-child,
  .affiliate-table-month {
    display: grid;
    grid-template-columns: minmax(88px, 0.85fr) minmax(0, 1.15fr);
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    min-width: 0;
    padding: 0.7rem 0.75rem;
    box-sizing: border-box;
    text-align: right;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .affiliate-table td::before {
    content: attr(data-label);
    min-width: 0;
    color: var(--clipr-text-tertiary);
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-align: left;
    text-transform: uppercase;
    overflow-wrap: anywhere;
  }

  .affiliate-table td .affiliate-status-badge {
    justify-self: end;
  }

  .affiliate-table tbody tr:last-child td:not(:last-child),
  .affiliate-table tbody tr td:not(:last-child) {
    border-bottom: 1px solid var(--clipr-border-color);
  }

  .affiliate-table tbody tr td:last-child {
    border-bottom: 0;
  }

  .affiliate-faq-backdrop {
    padding: 0.75rem;
  }

  .affiliate-faq-modal {
    max-height: calc(100dvh - 1.5rem);
    border-radius: var(--clipr-radius-lg);
  }

  .affiliate-faq-header {
    padding: 1rem 0.9rem 0.85rem;
  }

  .affiliate-faq-list {
    padding: 0.35rem 0.9rem 0.9rem;
  }

  .affiliate-faq-item {
    padding: 0.8rem;
  }
}
</style>
